import * as React from 'react';
import useSWR from 'swr';
import Alert from '@material-ui/lab/Alert';
import {
  Box,
  Card, CardContent,
  debounce,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import useSound from 'use-sound';

import type { IOpenedBox } from './OpenerInterfaces';
import scan from '../../assets/icons/scan.svg';
import openBox from '../../assets/icons/openBox.svg';
import BoxInformation from './BoxInformation';
import RepairItemsScanned from './RepairItemsScanned';
import useEventListener from '../../lib/useEventListener';
import prioritySound from '../../assets/sounds/priority.wav';

const useStyles = makeStyles(() => createStyles({
  root: {
    flexGrow: 1,
  },
  openerCard: {
    height: 180,
  },
  openerSvg: {
    height: 24,
    width: 24,
    marginBottom: 7,
  },
}));

const Opener: React.FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [playPriority] = useSound(prioritySound, { interrupt: false });

  const [boxId, setBoxId] = React.useState<string | undefined>();
  const [openBoxProcessing, setOpenBoxProcessing] = React.useState<boolean>(false);

  const [manuallyAddedRepairs, setManuallyAddedRepairs] = React.useState<{ barcode: string, storeRepairNumber: string, id: number }[]>(
    [{ barcode: '', storeRepairNumber: '', id: 0 }],
  );

  const openBoxHandler = debounce((scannedTrackingNumber: string) => {
    // if there's no content
    if (!scannedTrackingNumber) {
      return;
    }
    setOpenBoxProcessing(true);
    axios.post<{ boxId:string }>('/opener/openBox', {
      trackingNumber: scannedTrackingNumber,
    })
      .then((response) => {
        setBoxId(response.data.boxId);
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        setOpenBoxProcessing(false);
      });
  }, 500);

  function OpenBox() {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'To get started, scan in a box.'}</Alert>
        </Grid>
        <Grid item>
          {openBoxProcessing
            ? (
              <CircularProgress />
            )
            : (
              <TextField
                id="opener-scan-input"
                variant="outlined"
                size="small"
                onKeyDown={
              (event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  openBoxHandler((event.target as HTMLInputElement).value);
                }
              }
}
              />
            )}
        </Grid>
      </Grid>
    );
  }

  const fetcher = async (url: string, currentBoxId: string) => {
    setOpenBoxProcessing(true);
    const res = await axios.get<IOpenedBox>(url, {
      params: {
        boxId: currentBoxId,
      },
    })
      .then((response) => {
        if (response.data.isUnopened) {
          const freshRepairs = response.data.repairs.map(
            (r, i) => ({ barcode: r.barcode || '', storeRepairNumber: r.storeRepairNumber, id: i }),
          );
          setManuallyAddedRepairs(freshRepairs.length > 0 ? freshRepairs : [{ barcode: '', storeRepairNumber: '', id: 0 }]);
        }
        return response.data;
      });
    setOpenBoxProcessing(false);
    return res;
  };

  const { data, mutate } = useSWR<IOpenedBox>(boxId
    ? ['/opener/getBox', boxId]
    : null,
  (url, boxIdParameter) => fetcher(url, boxIdParameter),
  {
    revalidateOnFocus: false,
    revalidateOnMount: true,
    revalidateOnReconnect: false,
    refreshWhenOffline: false,
    refreshWhenHidden: false,
    refreshInterval: 0,
  });

  let barcodeBuffer = '';

  React.useEffect(() => {
    if (data?.isRush) {
      playPriority();
    }
  }, [data?.isRush, playPriority]);

  const handleOutScan = (scannedBarcode: string | '') => {
    axios.post('/scanner/LogManualScan', {
      ScanType: 'Out',
      ScanBody: scannedBarcode,
      Location: 'Open Repair',
    })
      .catch((er: AxiosError) => {
        enqueueSnackbar(er?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e?.key?.length === 1 || e?.key === 'Enter') {
      if (e.key !== 'Enter') {
        barcodeBuffer = `${barcodeBuffer}${e.key}`;
      } else {
        if (!e.defaultPrevented && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLButtonElement)) {
          openBoxHandler(barcodeBuffer);
        }
        barcodeBuffer = '';
      }
      if (barcodeBuffer.toLowerCase().includes('in:')) {
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('{*|}')) {
        openBoxHandler(barcodeBuffer.replace('{*|}', ''));
        barcodeBuffer = '';
      }
      if (barcodeBuffer.toLowerCase().includes('out:')) {
        barcodeBuffer = '';
      }
      if (barcodeBuffer.includes('<>??<>')) {
        handleOutScan(barcodeBuffer.replace('<>??<>', ''));
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  const RepairItemCount = ({ itemCount } : { itemCount:number }) => (
    <Card className={classes.openerCard}>
      <CardContent>
        <img src={scan} alt="scan.svg" className={classes.openerSvg} />
        <Typography>{'Repair Items in Current Box'}</Typography>
        <Typography variant="h2">{itemCount || 0}</Typography>
        <Typography variant="caption" color="textSecondary">{'Number of repair items that have been scanned in'}</Typography>
      </CardContent>
    </Card>
  );

  function BoxTrackingNumber() {
    return (
      <Card className={classes.openerCard}>
        <CardContent>
          <img src={openBox} alt="openBox.svg" className={classes.openerSvg} />
          <Typography gutterBottom>{'Current Box'}</Typography>
          <Typography variant="h4" className="verticalCenter">
            <Box component="span" mr={1}>{data?.trackingNumber}</Box>
            {data?.isRush && <ErrorIcon color="error" fontSize="medium" />}
          </Typography>
          <Typography variant="caption" color="textSecondary">{'Tracking Number'}</Typography>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <OpenBox />
    );
  }

  return (
    <Box className={classes.root}>
      <Grid container spacing={0}>
        <Grid item xs={1} />
        <Grid item xs={8} container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info">{'Open box and scan in repair item.'}</Alert>
          </Grid>
          <Grid item xs={6}>
            <RepairItemCount
              itemCount={data.isUnopened
                ? manuallyAddedRepairs.filter((map) => (map?.barcode !== '' || map?.storeRepairNumber !== '')).length
                : data?.repairs?.length}
            />
          </Grid>
          <Grid item xs={6}>
            <BoxTrackingNumber />
          </Grid>
          <Grid item xs={12}>
            <BoxInformation
              openedBox={data}
              mutate={mutate}
            />
          </Grid>
          { data?.store
          && (
            <Grid item xs={12}>
              <RepairItemsScanned
                manuallyAddedRepairs={manuallyAddedRepairs}
                setManuallyAddedRepairs={setManuallyAddedRepairs}
                openedBox={data}
                mutate={mutate}
              />
            </Grid>
          )}
        </Grid>
        <Grid item xs={3} />
      </Grid>
    </Box>
  );
};
export default Opener;
